import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../api/auth';
import { toast } from 'react-hot-toast';

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const IDLE_WARNING = 2 * 60 * 1000;  // Warn 2 minutes before

// Survives logout: per-user avatar URL stash. The backend currently doesn't
// echo avatarUrl in login / /auth/me responses, so without this the photo
// vanishes on re-login even though it's persisted in Cloudinary.
const avatarKey = (u) => (u?.id ? `avatar:${u.id}` : null);
const stashAvatar = (u) => {
    const k = avatarKey(u);
    if (k && u?.avatarUrl) localStorage.setItem(k, u.avatarUrl);
};
const hydrateAvatar = (u) => {
    if (!u || u.avatarUrl) return u;
    const k = avatarKey(u);
    const stashed = k ? localStorage.getItem(k) : null;
    return stashed ? { ...u, avatarUrl: stashed } : u;
};
const decodeJwtPayload = (token) => {
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(window.atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')));
    } catch {
        return null;
    }
};
const getGooglePicture = (credential) => decodeJwtPayload(credential)?.picture || '';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showIdleWarning, setShowIdleWarning] = useState(false);
    const idleTimer = useRef(null);
    const warningTimer = useRef(null);

    const performLogout = useCallback(() => {
        authAPI.logout();
        setUser(null);
        setShowIdleWarning(false);
        clearTimeout(idleTimer.current);
        clearTimeout(warningTimer.current);
    }, []);

    const logout = useCallback(() => {
        performLogout();
        toast.success('Logged out successfully', {
            style: { borderLeft: '4px solid var(--orange-9)', fontWeight: 500 },
        });
    }, [performLogout]);

    const resetIdleTimer = useCallback(() => {
        if (!user) return;
        setShowIdleWarning(false);
        clearTimeout(idleTimer.current);
        clearTimeout(warningTimer.current);

        // Show warning before timeout
        warningTimer.current = setTimeout(() => {
            setShowIdleWarning(true);
        }, IDLE_TIMEOUT - IDLE_WARNING);

        // Auto-logout on timeout
        idleTimer.current = setTimeout(() => {
            performLogout();
            toast.error('Session expired due to inactivity', {
                style: { borderLeft: '4px solid var(--red-9, #e5484d)', fontWeight: 500 },
                duration: 5000,
            });
            window.location.href = '/login';
        }, IDLE_TIMEOUT);
    }, [user, performLogout]);

    // Setup idle detection when user is logged in
    useEffect(() => {
        if (!user) return;

        const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
        const handleActivity = () => resetIdleTimer();

        events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
        resetIdleTimer();

        return () => {
            events.forEach(e => window.removeEventListener(e, handleActivity));
            clearTimeout(idleTimer.current);
            clearTimeout(warningTimer.current);
        };
    }, [user, resetIdleTimer]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            setLoading(false);
            return;
        }
        const localUser = hydrateAvatar(authAPI.getLocalUser());
        if (localUser) setUser(localUser);
        authAPI.getCurrentUser()
            .then((fresh) => {
                if (!fresh) return;
                const merged = hydrateAvatar({ ...(localUser || {}), ...fresh });
                setUser(merged);
                localStorage.setItem('user', JSON.stringify(merged));
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const login = async (credentials) => {
        try {
            const data = await authAPI.login(credentials);
            let fullUser = data.user;
            try {
                const fresh = await authAPI.getCurrentUser();
                if (fresh) fullUser = { ...data.user, ...fresh };
            } catch {}
            fullUser = hydrateAvatar(fullUser);
            if (fullUser) localStorage.setItem('user', JSON.stringify(fullUser));
            setUser(fullUser);
            toast.success(`Welcome, ${fullUser?.name || 'there'}!`, {
                style: { borderLeft: '4px solid var(--orange-9)', fontWeight: 500 },
            });
            return data;
        } catch (error) {
            throw error;
        }
    };

    const loginWithGoogle = async (credential) => {
        try {
            const googlePicture = getGooglePicture(credential);
            const data = await authAPI.googleLogin(credential);
            if (!data?.accessToken) return data;

            let fullUser = googlePicture && !data.user?.avatarUrl
                ? { ...data.user, avatarUrl: googlePicture }
                : data.user;
            try {
                const fresh = await authAPI.getCurrentUser();
                if (fresh) fullUser = { ...fullUser, ...fresh };
            } catch {}
            if (googlePicture && !fullUser?.avatarUrl) fullUser = { ...fullUser, avatarUrl: googlePicture };
            fullUser = hydrateAvatar(fullUser);
            stashAvatar(fullUser);
            if (fullUser) localStorage.setItem('user', JSON.stringify(fullUser));
            setUser(fullUser);
            toast.success(`Welcome, ${fullUser?.name || 'there'}!`, {
                style: { borderLeft: '4px solid var(--orange-9)', fontWeight: 500 },
            });
            return { ...data, user: fullUser };
        } catch (error) {
            throw error;
        }
    };

    const completeGoogleOnboarding = async (payload) => {
        try {
            const data = await authAPI.completeGoogleOnboarding(payload);
            let fullUser = payload.googlePicture && !data.user?.avatarUrl
                ? { ...data.user, avatarUrl: payload.googlePicture }
                : data.user;
            try {
                const fresh = await authAPI.getCurrentUser();
                if (fresh) fullUser = { ...fullUser, ...fresh };
            } catch {}
            if (payload.googlePicture && !fullUser?.avatarUrl) fullUser = { ...fullUser, avatarUrl: payload.googlePicture };
            fullUser = hydrateAvatar(fullUser);
            stashAvatar(fullUser);
            if (fullUser) localStorage.setItem('user', JSON.stringify(fullUser));
            setUser(fullUser);
            toast.success(`Welcome, ${fullUser?.name || 'there'}!`, {
                style: { borderLeft: '4px solid var(--orange-9)', fontWeight: 500 },
            });
            return { ...data, user: fullUser };
        } catch (error) {
            throw error;
        }
    };

    const register = async (userData) => {
        const data = await authAPI.register(userData);
        return data;
    };

    const updateUser = (updatedUser) => {
        if (!updatedUser) return;
        // Unwrap if the backend returns { user: {...} }
        const patch = updatedUser.user && typeof updatedUser.user === 'object'
            ? updatedUser.user
            : updatedUser;
        setUser((prev) => {
            const merged = { ...(prev || {}), ...patch };
            stashAvatar(merged);
            localStorage.setItem('user', JSON.stringify(merged));
            return merged;
        });
    };

    const value = {
        user,
        loading,
        login,
        loginWithGoogle,
        register,
        completeGoogleOnboarding,
        logout,
        updateUser,
        showIdleWarning,
        resetIdleTimer,
        isAuthenticated: !!user,
        isClient: user?.role === 'client',
        isProvider: user?.role === 'provider',
        isAdmin: user?.role === 'admin',
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
